import * as React from 'react';
import { act } from 'react-dom/test-utils';
import { createRendererWithUniDriver } from '../../test/utils';
import { textAreaDriverFactory } from './TextArea.driver';
import { TextArea, TextAreaTheme } from '.';
import { waitFor } from '@testing-library/react';

describe('TextArea', () => {
  const createDriver = createRendererWithUniDriver(textAreaDriverFactory);
  const ref: React.RefObject<TextArea> = React.createRef();

  it('should render label above the textarea', async () => {
    const onChange = jest.fn();
    const label = 'some-label';
    const { driver } = createDriver(
      <TextArea
        ariaLabel="test"
        value="Test"
        label={label}
        placeholder="Placeholder"
        onChange={onChange}
      />,
    );
    expect(await driver.label()).toBe(label);
  });

  it('should set lang to root', async () => {
    const lang = 'he';
    const onChange = jest.fn();

    const { driver } = createDriver(
      <TextArea
        lang={lang}
        onChange={onChange}
        value="Test"
        ariaLabel="test"
      />,
    );

    expect(await driver.getLang()).toBe(lang);
  });

  it('should render supplied value and placeholder', async () => {
    const onChange = jest.fn();
    const { driver } = createDriver(
      <TextArea
        ariaLabel="test"
        value="Test"
        onChange={onChange}
        placeholder="Placeholder"
      />,
    );
    expect(await driver.value()).toBe('Test');
    expect(await driver.placeholder()).toBe('Placeholder');
  });

  it('should hide success icon by default', async () => {
    const onChange = jest.fn();
    const { driver } = createDriver(
      <TextArea
        ariaLabel="test"
        value="Test"
        onChange={onChange}
        placeholder="Placeholder"
      />,
    );
    expect(await driver.success()).toBe(false);
  });

  it('should trigger onFocus', async () => {
    const onFocus = jest.fn((e) => expect(e.target.value).toBe('Test'));
    const { driver } = createDriver(
      <TextArea
        ariaLabel="test"
        value="Test"
        onFocus={onFocus}
        onChange={() => {}}
        placeholder="Placeholder"
        disabled={false}
      />,
    );

    await driver.focus();

    expect(onFocus).toHaveBeenCalled();
  });

  it('should trigger focus', async () => {
    const { driver } = createDriver(
      <TextArea ref={ref} ariaLabel="test" value="Test" onChange={() => {}} />,
    );

    expect(await driver.isFocused()).toEqual(false);
    await act(async () => ref.current.focus());
    expect(await driver.isFocused()).toEqual(true);
  });

  it('should trigger blur', async () => {
    const { driver } = createDriver(
      <TextArea ref={ref} ariaLabel="test" value="Test" onChange={() => {}} />,
    );

    await act(async () => ref.current.focus());
    expect(await driver.isFocused()).toEqual(true);
    await act(async () => ref.current.blur());
    expect(await driver.isFocused()).toEqual(false);
  });

  it('should trigger onChange', async () => {
    const onChange = jest.fn((e) => {
      expect(e.target.value).toBe('Test 1');
    });
    const { driver } = createDriver(
      <TextArea
        ariaLabel="test"
        value="Test"
        onChange={onChange}
        placeholder="Placeholder"
        disabled={false}
      />,
    );

    await driver.typeText('Test 1');
    expect(await driver.disabled()).toBe(false);
    expect(onChange).toHaveBeenCalled();
  });

  it('should not trigger onChange when disabled', async () => {
    const onChange = jest.fn();
    const { driver } = createDriver(
      <TextArea
        ariaLabel="test"
        value="Test"
        onChange={onChange}
        placeholder="Placeholder"
        disabled
      />,
    );

    await driver.typeText('Test 1');
    expect(await driver.disabled()).toBe(true);
    expect(onChange).not.toHaveBeenCalled();
  });

  it('should be able to show success icon', async () => {
    const onChange = jest.fn();
    const { driver } = createDriver(
      <TextArea
        ariaLabel="test"
        value="Test"
        onChange={onChange}
        placeholder="Placeholder"
        success
      />,
    );
    expect(await driver.success()).toBe(true);
  });

  it('should provide default theme', async () => {
    const onChange = jest.fn();
    const { driver } = createDriver(
      <TextArea
        ariaLabel="test"
        value="Test"
        onChange={onChange}
        placeholder="Placeholder"
        success
      />,
    );
    expect(await driver.theme()).toBe(TextAreaTheme.Box);
  });

  it('should accept theme', async () => {
    const onChange = jest.fn();
    const { driver } = createDriver(
      <TextArea
        ariaLabel="test"
        value="Test"
        onChange={onChange}
        theme={TextAreaTheme.Line}
        placeholder="Placeholder"
      />,
    );
    expect(await driver.theme()).toBe(TextAreaTheme.Line);
  });

  it('should hide error by default', async () => {
    const onChange = jest.fn();
    const { driver } = createDriver(
      <TextArea ariaLabel="test" value="Test" onChange={onChange} />,
    );
    expect(await driver.error()).toBe(false);
  });

  it('should show error with icon and description', async () => {
    const onChange = jest.fn();
    const { driver } = createDriver(
      <TextArea
        ariaLabel="test"
        error
        value="Test"
        onChange={onChange}
        errorMessage="error Message"
      />,
    );
    expect(await driver.error()).toBe(true);
    expect(await driver.errorIcon()).toBe(true);
  });

  it('should not show error icon in case when error description is not provided', async () => {
    const onChange = jest.fn();
    const { driver } = createDriver(
      <TextArea ariaLabel="test" error value="Test" onChange={onChange} />,
    );
    expect(await driver.error()).toBe(true);
    expect(await driver.errorIcon()).toBe(false);
  });

  it('should handle blur event', async () => {
    const onChange = jest.fn();
    const onBlur = jest.fn();
    const { driver } = createDriver(
      <TextArea
        ariaLabel="test"
        value="Test"
        onChange={onChange}
        onBlur={onBlur}
      />,
    );

    await driver.blur();

    expect(onBlur).toBeCalled();
  });

  it('should have maxLength attribute', async () => {
    const maxLength = 10;
    const { driver } = createDriver(
      <TextArea
        maxLength={maxLength}
        showCharCount
        value="value"
        ariaLabel="test"
        onChange={() => {}}
      />,
    );

    expect(await driver.getMaxLength()).toEqual(maxLength);
  });

  it('should show charCount as numOfChars/maxLength', async () => {
    const maxLength = 5;
    const value = 'abc';

    const { driver } = createDriver(
      <TextArea
        maxLength={maxLength}
        showCharCount
        value={value}
        ariaLabel="test"
        onChange={() => {}}
      />,
    );
    expect(await driver.isCharCountExist()).toBe(true);
    expect(await driver.getCharCountText()).toBe(
      `${value.length}/${maxLength}`,
    );
  });
  it('should not show charCount if maxLength is not passed', async () => {
    const { driver } = createDriver(
      <TextArea
        showCharCount
        value="value"
        ariaLabel="test"
        onChange={() => {}}
      />,
    );
    expect(await driver.isCharCountExist()).toBe(false);
  });

  describe('error tooltip', () => {
    const someErrorMessage = 'someErrorMessage';

    it('should toggle error tooltip when clicking on error icon', async () => {
      const { driver } = createDriver(
        <TextArea
          error
          value="value"
          ariaLabel="test"
          onChange={() => {}}
          errorMessage={someErrorMessage}
        />,
      );
      expect(await driver.isErrorTooltipShown()).toBeFalsy();

      await driver.clickOnErrorIcon();

      expect(await driver.isErrorTooltipShown()).toBeTruthy();

      await driver.clickOnErrorIcon();

      await waitFor(async () => {
        expect(await driver.isErrorTooltipShown()).toBeFalsy();
      });
    });

    it('should set the correct a11y attributes when old error', async () => {
      const { driver } = createDriver(
        <TextArea
          error
          errorMessage={someErrorMessage}
          value="value"
          ariaLabel="test"
          onChange={() => {}}
        />,
      );

      await driver.clickOnErrorIcon();

      expect(await driver.isErrorTooltipShown()).toBeTruthy();
      const inputAriaDescribedBy = await driver.ariaDescribedby();
      const errorIconAriaLabelledBy = await driver._errorIcon.getTooltipId();
      const tooltipContent = driver._errorIcon.getTooltipContent();
      expect(inputAriaDescribedBy).toBeDefined();
      expect(inputAriaDescribedBy).toBe(errorIconAriaLabelledBy);
      expect(await tooltipContent.text()).toBe(someErrorMessage);
    });

    it('aria describedby should be concatenated to value from props', async () => {
      const { driver } = createDriver(
        <TextArea
          error
          errorMessage={someErrorMessage}
          aria-describedby="some descriptions"
          value="value"
          ariaLabel="test"
          onChange={() => {}}
        />,
      );

      await driver.clickOnErrorIcon();

      expect(await driver.isErrorTooltipShown()).toBeTruthy();

      expect(await driver.getErrorMessage()).toBe(someErrorMessage);
    });

    it('should pass required to native element', async () => {
      const { driver } = createDriver(
        <TextArea
          ariaLabel="test"
          value="Test"
          label="some-label"
          placeholder="Placeholder"
          onChange={jest.fn()}
          required
        />,
      );
      expect(await driver.isRequired()).toBeTruthy();
    });

    it('should show an asterisk in the label when both required and showAsterisk are true', async () => {
      const { driver } = createDriver(
        <TextArea
          ariaLabel="test"
          value="Test"
          label="some-label"
          onChange={jest.fn()}
          required
          showAsterisk
        />,
      );
      expect(await driver.getLabelAsterisk().exists()).toBe(true);
    });
    it('should not show an asterisk in the label when only required is true', async () => {
      const { driver } = createDriver(
        <TextArea
          ariaLabel="test"
          value="Test"
          label="some-label"
          onChange={jest.fn()}
          required
        />,
      );
      expect(await driver.getLabelAsterisk().exists()).toBe(false);
    });
  });
});
